'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CreditCard,
  Download,
  ImageIcon,
  Loader2,
  Sparkles,
  User,
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { Link } from '@/core/i18n/navigation';
import { AIMediaType, AITaskStatus } from '@/extensions/ai/types';
import {
  ImageUploader,
  ImageUploaderValue,
  LazyImage,
} from '@/shared/blocks/common';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Label } from '@/shared/components/ui/label';
import { Progress } from '@/shared/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Textarea } from '@/shared/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { useAppContext } from '@/shared/contexts/app';
import { trackImageGeneration, trackFreeExampleGeneration, trackDailyCheckin } from '@/shared/lib/analytics';
import { Flame } from 'lucide-react';

interface ImageGeneratorProps {
  allowMultipleImages?: boolean;
  maxImages?: number;
  maxSizeMB?: number;
  srOnlyTitle?: string;
  className?: string;
  defaultExampleImage?: string;
  defaultPrompt?: string;
  title?: string;
}

interface GeneratedImage {
  id: string;
  url: string;
  provider?: string;
  model?: string;
  prompt?: string;
  isExample?: boolean; // 标记是否为示例生成
}

interface BackendTask {
  id: string;
  status: string;
  provider: string;
  model: string;
  prompt: string | null;
  taskInfo: string | null;
  taskResult: string | null;
}

interface CheckinStatus {
  hasCheckedInToday: boolean;
  consecutiveDays: number;
  tomorrowRewardCredits: number;
}

type ImageGeneratorTab = 'text-to-image' | 'image-to-image';

const POLL_INTERVAL = 5000;
const GENERATION_TIMEOUT = 180000;
const MAX_PROMPT_LENGTH = 2000;

// 示例图片和结果配置
const EXAMPLE_IMAGE_URL = 'https://r2.zestygen.com/effects/bf66ac00-281b-4ee2-b268-53a2fe79fae7.png';
const EXAMPLE_RESULT_URL = 'https://r2.zestygen.com/effects/bf66ac00-281b-4ee2-b268-53a2fe79fae7-0.png';
const EXAMPLE_PROMPT = 'Cinematic lighting';

// 图片比例选项
type AspectRatio = 'auto' | '2:3' | '3:2' | '1:1';

interface AspectRatioOption {
  id: AspectRatio;
  label: string;
  promptSuffix: string;
  icon: React.ReactNode;
}

const ASPECT_RATIO_OPTIONS: AspectRatioOption[] = [
  {
    id: 'auto',
    label: 'Auto',
    promptSuffix: '',
    icon: (
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />
      </svg>
    ),
  },
  {
    id: '2:3',
    label: '2:3',
    promptSuffix: ', portrait orientation, 2:3 aspect ratio',
    icon: (
      <div className="flex h-5 w-3.5 items-center justify-center rounded border border-current" />
    ),
  },
  {
    id: '3:2',
    label: '3:2',
    promptSuffix: ', landscape orientation, 3:2 aspect ratio',
    icon: (
      <div className="flex h-3.5 w-5 items-center justify-center rounded border border-current" />
    ),
  },
  {
    id: '1:1',
    label: '1:1',
    promptSuffix: ', square format, 1:1 aspect ratio',
    icon: (
      <div className="flex h-4 w-4 items-center justify-center rounded border border-current" />
    ),
  },
];

const MODEL_OPTIONS = [
  {
    value: 'bytedance/seedream-4',
    label: 'PRO',
    provider: 'replicate',
    scenes: ['text-to-image', 'image-to-image'],
    credits: 1,
  },
  {
    value: 'google/nano-banana-pro',
    label: 'MAX',
    provider: 'replicate',
    scenes: ['text-to-image', 'image-to-image'],
    credits: 5,
  },
];

const PROVIDER_OPTIONS = [
  {
    value: 'replicate',
    label: 'Replicate',
  },
  {
    value: 'fal',
    label: 'Fal',
  },
  // Gemini provider commented out
  // {
  //   value: 'gemini',
  //   label: 'Gemini',
  // },
  {
    value: 'kie',
    label: 'Kie',
  },
];

function parseTaskResult(taskResult: string | null): any {
  if (!taskResult) {
    return null;
  }

  try {
    return JSON.parse(taskResult);
  } catch (error) {
    console.warn('Failed to parse taskResult:', error);
    return null;
  }
}

function extractImageUrls(result: any): string[] {
  if (!result) {
    return [];
  }

  const output = result.output ?? result.images ?? result.data;

  if (!output) {
    return [];
  }

  if (typeof output === 'string') {
    return [output];
  }

  if (Array.isArray(output)) {
    return output
      .flatMap((item) => {
        if (!item) return [];
        if (typeof item === 'string') return [item];
        if (typeof item === 'object') {
          const candidate =
            item.url ?? item.uri ?? item.image ?? item.src ?? item.imageUrl;
          return typeof candidate === 'string' ? [candidate] : [];
        }
        return [];
      })
      .filter(Boolean);
  }

  if (typeof output === 'object') {
    const candidate =
      output.url ?? output.uri ?? output.image ?? output.src ?? output.imageUrl;
    if (typeof candidate === 'string') {
      return [candidate];
    }
  }

  return [];
}

export function ImageGenerator({
  allowMultipleImages = true,
  maxImages = 9,
  maxSizeMB = 5,
  srOnlyTitle,
  className,
  defaultExampleImage,
  defaultPrompt,
  title,
}: ImageGeneratorProps) {
  const t = useTranslations('ai.image.generator');
  const tCheckin = useTranslations('common.checkin-button');

  const [activeTab, setActiveTab] =
    useState<ImageGeneratorTab>('image-to-image');

  const [costCredits, setCostCredits] = useState<number>(5);
  const [provider] = useState('replicate'); // 固定使用 replicate
  const [model, setModel] = useState(MODEL_OPTIONS[0]?.value ?? '');
  const [prompt, setPrompt] = useState(defaultPrompt || '');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('auto');
  const [referenceImageItems, setReferenceImageItems] = useState<
    ImageUploaderValue[]
  >([]);
  const [referenceImageUrls, setReferenceImageUrls] = useState<string[]>([]);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(
    null
  );
  const [taskStatus, setTaskStatus] = useState<AITaskStatus | null>(null);
  const [downloadingImageId, setDownloadingImageId] = useState<string | null>(
    null
  );
  const [mounted, setMounted] = useState(false);
  const [hasUsedGenerator, setHasUsedGenerator] = useState(false); // 标记用户是否使用过生成器（点击过生成按钮）
  const [shouldShowDefaultImage, setShouldShowDefaultImage] = useState(true); // 控制是否显示默认示例图片

  // Checkin states
  const [isCheckinOpen, setIsCheckinOpen] = useState(false);
  const [isCheckinLoading, setIsCheckinLoading] = useState(false);
  const [checkinStatus, setCheckinStatus] = useState<CheckinStatus | null>(null);

  const { user, isCheckSign, setIsShowSignModal, fetchUserCredits } =
    useAppContext();

  // Fix hydration mismatch by ensuring client-side only rendering for auth-dependent UI
  useEffect(() => {
    setMounted(true);
  }, []);

  const promptLength = prompt.trim().length;
  const remainingCredits = user?.credits?.remainingCredits ?? 0;
  const isPromptTooLong = promptLength > MAX_PROMPT_LENGTH;
  const isTextToImageMode = activeTab === 'text-to-image';

  // 检查是否为示例配置（用户未使用过生成器时才可能是示例）
  const isExampleConfig =
    !hasUsedGenerator &&
    !isTextToImageMode &&
    referenceImageUrls.length === 1 &&
    referenceImageUrls[0] === EXAMPLE_IMAGE_URL &&
    prompt.trim() === EXAMPLE_PROMPT;

  // All image models (Nano Banana Pro, Seedream 4) cost $0.15 → 5 credits (70% margin)
  // Seedream 4 now costs 1 credit
  const IMAGE_CREDITS = 5;
  const SEEDREAM_CREDITS = 1;

  // Helper function to determine credits based on model
  const getCreditsForModel = (modelValue: string) => {
    const modelOption = MODEL_OPTIONS.find(opt => opt.value === modelValue);
    return modelOption?.credits ?? IMAGE_CREDITS;
  };

  // Set initial credits based on initial model
  useEffect(() => {
    if (model) {
      setCostCredits(getCreditsForModel(model));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (value: string) => {
    const tab = value as ImageGeneratorTab;
    setActiveTab(tab);

    const availableModels = MODEL_OPTIONS.filter(
      (option) => option.scenes.includes(tab) && option.provider === provider
    );

    if (availableModels.length > 0) {
      const newModel = availableModels[0].value;
      setModel(newModel);
      setCostCredits(getCreditsForModel(newModel));
    } else {
      setModel('');
      setCostCredits(IMAGE_CREDITS);
    }
  };

  const handleModelChange = (value: string) => {
    setModel(value);
    setCostCredits(getCreditsForModel(value));
  };

  const taskStatusLabel = useMemo(() => {
    if (!taskStatus) {
      return '';
    }

    switch (taskStatus) {
      case AITaskStatus.PENDING:
        return 'Waiting for the model to start';
      case AITaskStatus.PROCESSING:
        return 'Generating your image...';
      case AITaskStatus.SUCCESS:
        return 'Image generation completed';
      case AITaskStatus.FAILED:
        return 'Generation failed';
      default:
        return '';
    }
  }, [taskStatus]);

  const handleReferenceImagesChange = useCallback(
    (items: ImageUploaderValue[]) => {
      setReferenceImageItems(items);
      const uploadedUrls = items
        .filter((item) => item.status === 'uploaded' && item.url)
        .map((item) => item.url as string);
      setReferenceImageUrls(uploadedUrls);

      // 用户上传了图片，不再显示默认示例图片
      if (items.length > 0) {
        setShouldShowDefaultImage(false);
      }
    },
    []
  );

  // 初始化默认提示词（仅在组件首次挂载时执行一次）
  useEffect(() => {
    if (defaultPrompt && !prompt) {
      setPrompt(defaultPrompt);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 当 defaultPreviews 传入时，同步初始化 referenceImageUrls
  useEffect(() => {
    if (shouldShowDefaultImage && defaultExampleImage && referenceImageUrls.length === 0) {
      setReferenceImageUrls([defaultExampleImage]);
    }
  }, [shouldShowDefaultImage, defaultExampleImage, referenceImageUrls.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const isReferenceUploading = useMemo(
    () => referenceImageItems.some((item) => item.status === 'uploading'),
    [referenceImageItems]
  );

  const hasReferenceUploadError = useMemo(
    () => referenceImageItems.some((item) => item.status === 'error'),
    [referenceImageItems]
  );

  const resetTaskState = useCallback(() => {
    setIsGenerating(false);
    setProgress(0);
    setTaskId(null);
    setGenerationStartTime(null);
    setTaskStatus(null);
  }, []);

  const pollTaskStatus = useCallback(
    async (id: string) => {
      try {
        if (
          generationStartTime &&
          Date.now() - generationStartTime > GENERATION_TIMEOUT
        ) {
          resetTaskState();
          toast.error('Image generation timed out. Please try again.');
          return true;
        }

        const resp = await fetch('/api/ai/query', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ taskId: id }),
        });

        if (!resp.ok) {
          throw new Error(`request failed with status: ${resp.status}`);
        }

        const { code, message, data } = await resp.json();
        if (code !== 0) {
          throw new Error(message || 'Query task failed');
        }

        const task = data as BackendTask;
        const currentStatus = task.status as AITaskStatus;
        setTaskStatus(currentStatus);

        const parsedResult = parseTaskResult(task.taskInfo);
        const imageUrls = extractImageUrls(parsedResult);

        if (currentStatus === AITaskStatus.PENDING) {
          setProgress((prev) => Math.max(prev, 20));
          return false;
        }

        if (currentStatus === AITaskStatus.PROCESSING) {
          if (imageUrls.length > 0) {
            setGeneratedImages(
              imageUrls.map((url, index) => ({
                id: `${task.id}-${index}`,
                url,
                provider: task.provider,
                model: task.model,
                prompt: task.prompt ?? undefined,
              }))
            );
            setProgress((prev) => Math.max(prev, 85));
          } else {
            setProgress((prev) => Math.min(prev + 10, 80));
          }
          return false;
        }

        if (currentStatus === AITaskStatus.SUCCESS) {
          if (imageUrls.length === 0) {
            toast.error('The provider returned no images. Please retry.');
          } else {
            setGeneratedImages(
              imageUrls.map((url, index) => ({
                id: `${task.id}-${index}`,
                url,
                provider: task.provider,
                model: task.model,
                prompt: task.prompt ?? undefined,
              }))
            );
            toast.success('Image generated successfully');

            // Track image generation for analytics
            trackImageGeneration({
              model: task.model,
              credits_used: costCredits,
              scene: activeTab,
            });
          }

          setProgress(100);
          resetTaskState();
          return true;
        }

        if (currentStatus === AITaskStatus.FAILED) {
          const errorMessage =
            parsedResult?.errorMessage || 'Generate image failed';
          toast.error(errorMessage);
          resetTaskState();

          fetchUserCredits();

          return true;
        }

        setProgress((prev) => Math.min(prev + 5, 95));
        return false;
      } catch (error: any) {
        console.error('Error polling image task:', error);
        toast.error(`Query task failed: ${error.message}`);
        resetTaskState();

        fetchUserCredits();

        return true;
      }
    },
    [generationStartTime, resetTaskState, costCredits, activeTab]
  );

  useEffect(() => {
    if (!taskId || !isGenerating) {
      return;
    }

    let cancelled = false;

    const tick = async () => {
      if (!taskId) {
        return;
      }
      const completed = await pollTaskStatus(taskId);
      if (completed) {
        cancelled = true;
      }
    };

    tick();

    const interval = setInterval(async () => {
      if (cancelled || !taskId) {
        clearInterval(interval);
        return;
      }
      const completed = await pollTaskStatus(taskId);
      if (completed) {
        clearInterval(interval);
      }
    }, POLL_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [taskId, isGenerating, pollTaskStatus]);

  const handleGenerate = async () => {
    if (!user) {
      setIsShowSignModal(true);
      return;
    }

    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt) {
      toast.error('Please enter a prompt before generating.');
      return;
    }

    // 标记用户已经使用过生成器
    setHasUsedGenerator(true);
    // 注意：不在这里清除 shouldShowDefaultImage，让示例图片保留以供对比

    // 检查是否为示例生成
    const isExampleGeneration =
      !isTextToImageMode &&
      referenceImageUrls.length === 1 &&
      referenceImageUrls[0] === EXAMPLE_IMAGE_URL &&
      trimmedPrompt === EXAMPLE_PROMPT;

    // 如果是示例生成，执行模拟逻辑
    if (isExampleGeneration) {
      setIsGenerating(true);
      setProgress(15);
      setTaskStatus(AITaskStatus.PENDING);
      setGeneratedImages([]);
      setGenerationStartTime(Date.now());

      // 模拟生成过程
      setTimeout(() => {
        setProgress(50);
        setTaskStatus(AITaskStatus.PROCESSING);
      }, 800);

      setTimeout(() => {
        setProgress(85);
      }, 1800);

      setTimeout(() => {
        // 显示示例结果
        setGeneratedImages([
          {
            id: 'example-result',
            url: EXAMPLE_RESULT_URL,
            provider: provider,
            model: model,
            prompt: trimmedPrompt,
            isExample: true,
          },
        ]);
        setProgress(100);
        setTaskStatus(AITaskStatus.SUCCESS);
        toast.success('Image generated successfully');
        resetTaskState();

        // Track free example generation
        trackFreeExampleGeneration({
          model: model,
          scene: activeTab,
        });
      }, 3000);

      return;
    }

    // 正常生成流程
    if (remainingCredits < costCredits) {
      toast.error('Insufficient credits. Please top up to keep creating.');
      return;
    }

    // 将比例信息组装到 prompt 中
    const aspectRatioOption = ASPECT_RATIO_OPTIONS.find(opt => opt.id === aspectRatio);
    const finalPrompt = trimmedPrompt + (aspectRatioOption?.promptSuffix || '');

    if (!provider || !model) {
      toast.error('Provider or model is not configured correctly.');
      return;
    }

    if (!isTextToImageMode && referenceImageUrls.length === 0) {
      toast.error('Please upload reference images before generating.');
      return;
    }

    setIsGenerating(true);
    setProgress(15);
    setTaskStatus(AITaskStatus.PENDING);
    setGeneratedImages([]);
    setGenerationStartTime(Date.now());

    try {
      const options: any = {};

      if (!isTextToImageMode) {
        options.image_input = referenceImageUrls;
      }

      const resp = await fetch('/api/ai/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mediaType: AIMediaType.IMAGE,
          scene: isTextToImageMode ? 'text-to-image' : 'image-to-image',
          provider,
          model,
          prompt: finalPrompt,
          options,
        }),
      });

      if (!resp.ok) {
        throw new Error(`request failed with status: ${resp.status}`);
      }

      const { code, message, data } = await resp.json();
      if (code !== 0) {
        throw new Error(message || 'Failed to create an image task');
      }

      const newTaskId = data?.id;
      if (!newTaskId) {
        throw new Error('Task id missing in response');
      }

      if (data.status === AITaskStatus.SUCCESS && data.taskInfo) {
        const parsedResult = parseTaskResult(data.taskInfo);
        const imageUrls = extractImageUrls(parsedResult);

        if (imageUrls.length > 0) {
          setGeneratedImages(
            imageUrls.map((url, index) => ({
              id: `${newTaskId}-${index}`,
              url,
              provider,
              model,
              prompt: finalPrompt,
            }))
          );
          toast.success('Image generated successfully');
          setProgress(100);
          resetTaskState();
          await fetchUserCredits();
          return;
        }
      }

      setTaskId(newTaskId);
      setProgress(25);

      await fetchUserCredits();
    } catch (error: any) {
      console.error('Failed to generate image:', error);
      toast.error(`Failed to generate image: ${error.message}`);
      resetTaskState();
    }
  };

  const handleDownloadImage = async (image: GeneratedImage) => {
    if (!image.url) {
      return;
    }

    try {
      setDownloadingImageId(image.id);
      // fetch image via proxy
      const resp = await fetch(
        `/api/proxy/file?url=${encodeURIComponent(image.url)}`
      );
      if (!resp.ok) {
        throw new Error('Failed to fetch image');
      }

      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${image.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 200);
      toast.success('Image downloaded');
    } catch (error) {
      console.error('Failed to download image:', error);
      toast.error('Failed to download image');
    } finally {
      setDownloadingImageId(null);
    }
  };

  // Handle opening checkin dialog
  const handleOpenCheckinDialog = async () => {
    if (!user) {
      // 未登录，显示登录弹窗
      setIsShowSignModal(true);
      return;
    }

    setIsCheckinOpen(true);

    // 获取签到状态
    try {
      const response = await fetch('/api/checkin/claim?action=status');
      if (response.ok) {
        const status = await response.json();
        setCheckinStatus(status);
      }
    } catch (error) {
      console.error('Failed to fetch checkin status:', error);
    }
  };

  // Handle checkin
  const handleCheckin = async () => {
    if (!user) {
      setIsCheckinOpen(false);
      return;
    }

    setIsCheckinLoading(true);
    try {
      const response = await fetch('/api/checkin/claim', {
        method: 'POST',
      });

      if (!response.ok) {
        const error = await response.json();

        // 埋点：签到失败
        trackDailyCheckin({
          credits_earned: 0,
          consecutive_days: checkinStatus?.consecutiveDays || 0,
          success: false,
          error_message: error.error || tCheckin('checkin-failed'),
        });

        throw new Error(error.error || tCheckin('checkin-failed'));
      }

      const result = await response.json();
      const dayKey = Math.min(result.consecutiveDays, 5);

      // 埋点：签到成功
      trackDailyCheckin({
        credits_earned: result.rewardCredits,
        consecutive_days: result.consecutiveDays,
        success: true,
      });

      // 使用新的成功文案
      toast.success(tCheckin(`day_${dayKey}.success_title`), {
        description: tCheckin(`day_${dayKey}.success_reward`),
        duration: 3000,
      });

      // 更新状态
      setCheckinStatus({
        hasCheckedInToday: true,
        consecutiveDays: result.consecutiveDays,
        tomorrowRewardCredits: Math.min(result.consecutiveDays + 1, 5),
      });

      // 刷新用户积分
      await fetchUserCredits();

      // 延迟关闭对话框
      setTimeout(() => {
        setIsCheckinOpen(false);
      }, 2000);
    } catch (error: any) {
      toast.error(tCheckin('checkin-failed'), {
        description: error.message,
      });
    } finally {
      setIsCheckinLoading(false);
    }
  };

  return (
    <section className={cn('py-16 md:py-24', className)}>
      <div className="container">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <Card>

              <CardContent className="space-y-6 pb-8">
                <Tabs value={activeTab} onValueChange={handleTabChange}>
                  <TabsList className="bg-primary/10 grid w-full grid-cols-2">
                    <TabsTrigger value="text-to-image" aria-label={t('tabs.text-to-image')}>
                      {t('tabs.text-to-image')}
                    </TabsTrigger>
                    <TabsTrigger value="image-to-image" aria-label={t('tabs.image-to-image')}>
                      {t('tabs.image-to-image')}
                    </TabsTrigger>
                  </TabsList>
                  {/* Radix tabs expects content panels; keep them hidden to avoid invalid aria-controls */}
                  <TabsContent value="text-to-image" className="hidden" />
                  <TabsContent value="image-to-image" className="hidden" />
                </Tabs>

                {!isTextToImageMode && (
                  <div className="space-y-4">
                    <ImageUploader
                      title={t('form.reference_image')}
                      allowMultiple={allowMultipleImages}
                      maxImages={allowMultipleImages ? maxImages : 1}
                      maxSizeMB={maxSizeMB}
                      onChange={handleReferenceImagesChange}
                      emptyHint={t('form.reference_image_placeholder')}
                      defaultPreviews={
                        shouldShowDefaultImage && defaultExampleImage
                          ? [defaultExampleImage]
                          : undefined
                      }
                    />

                    {hasReferenceUploadError && (
                      <p className="text-destructive text-xs">
                        {t('form.some_images_failed_to_upload')}
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="image-prompt">{t('form.prompt')}</Label>
                  <Textarea
                    id="image-prompt"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={t('form.prompt_placeholder')}
                    className="min-h-32"
                  />
                  <div className="text-muted-foreground flex items-center justify-between text-xs">
                    <span>
                      {promptLength} / {MAX_PROMPT_LENGTH}
                    </span>
                    {isPromptTooLong && (
                      <span className="text-destructive">
                        {t('form.prompt_too_long')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('form.model')}</Label>
                  <Select value={model} onValueChange={handleModelChange}>
                    <SelectTrigger className="h-auto w-full py-3.5" aria-label={t('form.model')}>
                      <SelectValue placeholder={t('form.select_model')}>
                        {(() => {
                          const selectedModel = MODEL_OPTIONS.find(opt => opt.value === model);
                          return selectedModel ? (
                            <div className="flex flex-col items-start gap-1">
                              <span className="font-medium leading-none">{selectedModel.label}</span>
                              <span className="text-muted-foreground text-xs font-normal leading-tight">
                                {t(`form.model_${selectedModel.label.toLowerCase()}_description`)}
                              </span>
                            </div>
                          ) : null;
                        })()}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {MODEL_OPTIONS.filter(
                        (option) =>
                          option.scenes.includes(activeTab) &&
                          option.provider === provider
                      ).map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex flex-col items-start gap-0.5">
                            <span className="font-medium">{option.label}</span>
                            <span className="text-muted-foreground text-xs">
                              {t(`form.model_${option.label.toLowerCase()}_description`)}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Aspect Ratio Selector */}
                <div className="space-y-2">
                  <Label>{t('form.aspect_ratio')}</Label>
                  <div className="flex gap-2">
                    {ASPECT_RATIO_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setAspectRatio(option.id)}
                        className={cn(
                          'flex flex-1 flex-col items-center justify-center gap-1.5 rounded-lg border-2 p-3 transition-all',
                          aspectRatio === option.id
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-muted hover:border-muted-foreground/30 text-muted-foreground hover:text-foreground'
                        )}
                      >
                        {option.icon}
                        <span className="text-xs font-medium">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {!mounted || isCheckSign ? (
                  <Button className="w-full" disabled size="lg">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('checking_account')}
                  </Button>
                ) : user ? (
                  <Button
                    size="lg"
                    className="w-full"
                    onClick={handleGenerate}
                    disabled={
                      isGenerating ||
                      !prompt.trim() ||
                      isPromptTooLong ||
                      isReferenceUploading ||
                      hasReferenceUploadError
                    }
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('generating')}
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        {isExampleConfig ? t('generate_example') : t('generate')}
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    className="w-full"
                    onClick={() => setIsShowSignModal(true)}
                  >
                    <User className="mr-2 h-4 w-4" />
                    {t('sign_in_to_generate')}
                  </Button>
                )}

                {user && remainingCredits > 0 ? (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-primary">
                      {isExampleConfig
                        ? t('credits_cost_free')
                        : t('credits_cost', { credits: costCredits })}
                    </span>
                    {!isExampleConfig && (
                      <span>
                        {t('credits_remaining', { credits: remainingCredits })}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-primary">
                        {isExampleConfig
                          ? t('credits_cost_free')
                          : t('credits_cost', { credits: costCredits })}
                      </span>
                      {!isExampleConfig && (
                        <span>
                          {t('credits_remaining', { credits: remainingCredits })}
                        </span>
                      )}
                    </div>
                    {!isExampleConfig && (
                      <Link href="/pricing">
                        <Button variant="outline" className="w-full" size="lg">
                          <CreditCard className="mr-2 h-4 w-4" />
                          {t('buy_credits')}
                        </Button>
                      </Link>
                    )}
                  </div>
                )}

                {isGenerating && (
                  <div className="space-y-2 rounded-lg border p-4">
                    <div className="flex items-center justify-between text-sm">
                      <span>{t('progress')}</span>
                      <span>{progress}%</span>
                    </div>
                    <Progress value={progress} />
                    {taskStatusLabel && (
                      <p className="text-muted-foreground text-center text-xs">
                        {taskStatusLabel}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                  <ImageIcon className="h-5 w-5" />
                  {t('generated_images')}
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-8">
                {generatedImages.length > 0 ? (
                  <>
                    {title && (
                      <div className="mb-6 flex items-center justify-center">
                        <h2 className="text-2xl font-semibold">{title}</h2>
                      </div>
                    )}
                  <div
                    className={
                      generatedImages.length === 1
                        ? 'grid grid-cols-1 gap-6'
                        : 'grid gap-6 sm:grid-cols-2'
                    }
                  >
                    {generatedImages.map((image) => (
                      <div key={image.id} className="space-y-3">
                        <div
                          className={
                            generatedImages.length === 1
                              ? 'relative overflow-hidden rounded-lg border'
                              : 'relative aspect-square overflow-hidden rounded-lg border'
                          }
                        >
                          <LazyImage
                            src={image.url}
                            alt={image.prompt || 'Generated image'}
                            className={
                              generatedImages.length === 1
                                ? 'h-auto w-full'
                                : 'h-full w-full object-cover'
                            }
                          />

                          <div className="absolute right-2 bottom-2 flex justify-end text-sm">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="ml-auto"
                              onClick={() => handleDownloadImage(image)}
                              disabled={downloadingImageId === image.id}
                            >
                              {downloadingImageId === image.id ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                </>
                              ) : (
                                <>
                                  <Download className="h-4 w-4" />
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                        {image.isExample && (
                          <div className="text-muted-foreground text-center text-xs">
                            {t('example_result_label')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Checkin Prompt - Shows after image generation */}
                  <div className="mt-6 rounded-lg border border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10 p-4">
                    <div className="text-center space-y-2">
                      <p className="text-sm font-medium text-foreground">
                        {t('checkin_prompt.title')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('checkin_prompt.features')}
                      </p>
                      <Button
                        size="sm"
                        variant="default"
                        className="mt-2"
                        onClick={handleOpenCheckinDialog}
                      >
                        {t('checkin_prompt.cta')}
                      </Button>
                    </div>
                  </div>
                  </>
                ) : (
                  <>
                    {title && (
                      <div className="mb-6 flex items-center justify-center">
                        <h2 className="text-2xl font-semibold">{title}</h2>
                      </div>
                    )}
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="bg-muted mb-4 flex h-16 w-16 items-center justify-center rounded-full">
                      <ImageIcon className="text-muted-foreground h-10 w-10" />
                    </div>
                    <p className="text-muted-foreground">
                      {isGenerating
                        ? t('ready_to_generate')
                        : t('no_images_generated')}
                    </p>
                  </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Checkin Dialog */}
      {mounted && (
        <Dialog open={isCheckinOpen} onOpenChange={setIsCheckinOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold">
                <Flame className="inline h-5 w-5 text-orange-500 mr-2" />
                {tCheckin('title')}
              </DialogTitle>
              <DialogDescription className="text-sm">
                {tCheckin('subtitle')}
              </DialogDescription>
            </DialogHeader>

            {checkinStatus ? (
              <div className="space-y-5">
                {/* 今日奖励卡片 - 未签到时显示 */}
                {!checkinStatus.hasCheckedInToday && (
                  <div className="rounded-xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-5 space-y-3">
                    <h3 className="text-base font-semibold text-foreground">
                      {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays + 1, 5)}.reward_title`)}
                    </h3>
                    <div className="space-y-2">
                      <p className="text-lg font-bold text-primary">
                        {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays + 1, 5)}.reward_main`)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays + 1, 5)}.reward_sub`)}
                      </p>
                      <p className="text-xs text-muted-foreground/80">
                        {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays + 1, 5)}.reward_desc`)}
                      </p>
                    </div>
                  </div>
                )}

                {/* 签到成功卡片 - 已签到时显示 */}
                {checkinStatus.hasCheckedInToday && (
                  <div className="rounded-xl border-2 border-green-500/30 bg-gradient-to-br from-green-50 via-green-100/50 to-green-50 dark:from-green-950/20 dark:via-green-900/30 dark:to-green-950/20 p-5 space-y-3">
                    <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                      <span className="text-2xl">🎉</span>
                      {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays, 5)}.success_title`)}
                    </h3>
                    <div className="space-y-2">
                      <p className="text-lg font-bold text-green-600 dark:text-green-400">
                        {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays, 5)}.success_reward`)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays, 5)}.success_available`)}
                      </p>
                    </div>
                  </div>
                )}

                {/* 明日预告 - 仅在未达到第5天时显示 */}
                {!checkinStatus.hasCheckedInToday && checkinStatus.consecutiveDays < 4 && (
                  <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">
                      {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays + 1, 5)}.tomorrow_preview`)}
                    </h4>
                    <p className="text-sm font-semibold text-foreground">
                      {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays + 2, 5)}.reward_main`)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays + 2, 5)}.reward_sub`)}
                    </p>
                  </div>
                )}

                {/* 连续天数统计 */}
                <div className="flex items-center justify-center gap-4 py-2">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {checkinStatus.consecutiveDays}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {tCheckin('consecutive-days')}
                    </div>
                  </div>
                  {checkinStatus.hasCheckedInToday && (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-500">✓</div>
                      <div className="text-xs text-muted-foreground">
                        {tCheckin('status')}
                      </div>
                    </div>
                  )}
                </div>

                {/* 签到按钮 */}
                <Button
                  onClick={handleCheckin}
                  disabled={checkinStatus.hasCheckedInToday || isCheckinLoading}
                  size="lg"
                  className="w-full text-base font-semibold"
                >
                  {checkinStatus.hasCheckedInToday
                    ? tCheckin('button-checked')
                    : tCheckin(`day_${Math.min(checkinStatus.consecutiveDays + 1, 5)}.cta`)}
                </Button>

                {/* 查看详情链接 */}
                <div className="text-center">
                  <Link
                    href="/settings/checkin"
                    className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                    onClick={() => setIsCheckinOpen(false)}
                  >
                    {tCheckin('view-details')} →
                  </Link>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-sm text-muted-foreground">
                  {tCheckin('loading')}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}
